import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import Layout, { PageHeader, Grid4, MetricCard, Panel, Lbl, Inp, Btn, Err, Ok, Empty, useTabs, Keep } from './Layout'
import ScheduleClasses from './ScheduleClasses'
import ScheduleDemo    from './ScheduleDemo'
import Calendar        from './Calendar'
import Resources       from './Resources'
import SupportChat     from './SupportChat'

const STATUSES = ['New','Contacted','Demo Scheduled','Enrolled','Lost']

const STATUS_META = {
  'New':            { bg:'rgba(99,102,241,0.15)',  text:'#a78bfa', icon:'🆕' },
  'Contacted':      { bg:'rgba(59,130,246,0.15)',  text:'#60a5fa', icon:'📞' },
  'Demo Scheduled': { bg:'rgba(245,158,11,0.15)',  text:'#fbbf24', icon:'📅' },
  'Enrolled':       { bg:'rgba(16,185,129,0.15)',  text:'#34d399', icon:'✅' },
  'Lost':           { bg:'rgba(239,68,68,0.15)',   text:'#f87171', icon:'❌' },
}

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768)
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  return m
}

function getPhone(lead) {
  return lead.phone || lead.phone_number || lead.mobile || lead.contact || lead.whatsapp || ''
}

function fmtDate(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
}
function fmtTime(ts) {
  if (!ts) return ''
  return new Date(ts).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })
}

// What this salesperson may use. Admin sets these per person in
// Admin → Sales Access; missing values (older profiles) count as allowed,
// except Schedule Class, which has always been opt-in.
export function salesAccess(profile) {
  return {
    leads:   profile?.sales_can_view_leads    !== false,
    demo:    profile?.sales_can_schedule_demo !== false,
    contact: profile?.sales_can_see_contact   !== false,
    classes: !!profile?.can_schedule_classes,
  }
}

export default function SalesDash({ profile }) {
  const access = salesAccess(profile)
  const isMobile = useIsMobile()

  const NAV = [
    ...(access.leads   ? [{ id:'leads',    icon:'📋', nav:'leads', label:'Leads'          }] : []),
    ...(access.demo    ? [{ id:'demo',     icon:'▶',  nav:'play',  label:'Schedule Demo'  }] : []),
    { id:'support', icon:'💬', nav:'chat', label:'Support' },
    ...(access.classes ? [{ id:'newclass', icon:'📅', nav:'cal',   label:'Schedule Class' }] : []),
  ]
  const { tab, go: setTab, has: visited } = useTabs(NAV[0].id)
  const sidebarItems = NAV.map(t => ({ id: t.id, icon: t.nav, label: t.label }))

  return (
    <Layout profile={profile} pageTitle="Sales CRM" activeTab={tab} onTabChange={setTab} navItems={sidebarItems}>
      <PageHeader title="Sales Pipeline" subtitle="Manage leads, demos, and classes." />

      {/* Phones use the bottom menu from Layout, so the tab row is desktop only. */}
      {!isMobile && <div style={{ display:'flex', gap:'6px', marginBottom:'16px', flexWrap:'wrap' }}>
        {NAV.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            fontSize: isMobile ? '11px' : '13px',
            fontWeight: 600,
            padding: isMobile ? '7px 10px' : '8px 16px',
            borderRadius:'10px', border:'none', cursor:'pointer',
            background: tab === t.id ? '#1e90ff' : 'rgba(var(--fg-rgb),0.06)',
            color: tab === t.id ? '#fff' : 'rgba(var(--fg-rgb),var(--a55))',
            transition:'all .15s',
            display:'flex', alignItems:'center', gap:'4px',
          }}>
            <span>{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </div>}

      {access.leads   && visited('leads')    && <Keep show={tab === 'leads'}><LeadsSection  profile={profile} isMobile={isMobile} showContact={access.contact} /></Keep>}
      {access.demo    && visited('demo')     && <Keep show={tab === 'demo'}><ScheduleDemo  profile={profile} showContact={access.contact} /></Keep>}
      {visited('support') && <Keep show={tab === 'support'}><SupportChat profile={profile} /></Keep>}
      {access.classes && visited('newclass') && <Keep show={tab === 'newclass'}><ScheduleClasses profile={profile} /></Keep>}

      {/* FIX #6: Calendar and Resources only render inside the component tree, */}
      {/* not unconditionally outside the tab system */}
    </Layout>
  )
}

// Leads are grouped by the day they came in (like Admin → Leads) when
// sorted by date. A lead counts toward a salesperson's own "Demo Scheduled"
// and "Enrolled" numbers once they move it to that status (assigned_to).
function dayKey(ts) {
  if (!ts) return 'Unknown date'
  return new Date(ts).toLocaleDateString('en-IN', { weekday:'short', day:'2-digit', month:'short', year:'numeric' })
}

// ── LEADS SECTION ─────────────────────────────────────────────────────────────
function LeadsSection({ profile, isMobile, showContact }) {
  const [leads,        setLeads]        = useState([])
  const [loading,      setLoading]      = useState(true)
  const [expandedId,   setExpandedId]   = useState(null)
  const [noteText,     setNoteText]     = useState('')
  const [savingNote,   setSavingNote]   = useState(false)
  const [statusSaving, setStatusSaving] = useState(null)
  const [search,       setSearch]       = useState('')
  const [sortBy,       setSortBy]       = useState('newest')
  const [err,          setErr]          = useState('')
  const [openDays,     setOpenDays]     = useState(null) // null = only the newest day open

  const SORT_OPTIONS = [
    { value:'newest',   label:'📅 Newest First'  },
    { value:'oldest',   label:'📅 Oldest First'  },
    { value:'name_az',  label:'🔤 Name A → Z'    },
    { value:'name_za',  label:'🔤 Name Z → A'    },
    { value:'status',   label:'🏷 By Status'     },
    { value:'phone',    label:'📞 By Phone'       },
    { value:'number',   label:'🔢 By Number'      },
  ]

  // Every salesperson sees every lead, so new sales users see the full list
  // straight away. New leads (demo form, WhatsApp, Meta ads) arrive with no
  // assigned_to, so filtering by it left the list empty. Access is limited
  // to admin/sales by the "Sales and admins manage leads" RLS policy.
  const fetchLeads = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) setErr(error.message)
    else setLeads(data || [])
    setLoading(false)
  }, [profile.id])

  useEffect(() => { fetchLeads() }, [fetchLeads])

  const q = search.trim().toLowerCase()
  const filtered = q
    ? leads.filter(l =>
        (l.full_name   || '').toLowerCase().includes(q) ||
        (showContact && (getPhone(l) || '').toLowerCase().includes(q)) ||
        (showContact && (l.email     || '').toLowerCase().includes(q)) ||
        (l.source      || '').toLowerCase().includes(q) ||
        (l.notes       || '').toLowerCase().includes(q) ||
        (l.course_interest || '').toLowerCase().includes(q)
      )
    : leads

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'oldest':   return new Date(a.created_at) - new Date(b.created_at)
      case 'name_az':  return (a.full_name || '').localeCompare(b.full_name || '')
      case 'name_za':  return (b.full_name || '').localeCompare(a.full_name || '')
      case 'status':   return STATUSES.indexOf(a.status) - STATUSES.indexOf(b.status)
      case 'phone':    return getPhone(a).localeCompare(getPhone(b))
      case 'number':   return (a.id || 0) - (b.id || 0)
      default:         return new Date(b.created_at) - new Date(a.created_at)
    }
  })

  async function updateStatus(leadId, status) {
    setStatusSaving(leadId)
    // Moving a lead to Demo Scheduled / Enrolled credits it to this person.
    const credit = status === 'Demo Scheduled' || status === 'Enrolled'
    const changes = credit ? { status, assigned_to: profile.id } : { status }
    const { error } = await supabase.from('leads').update(changes).eq('id', leadId)
    if (error) setErr(error.message)
    else setLeads(prev => prev.map(l => l.id === leadId ? { ...l, ...changes } : l))
    setStatusSaving(null)
  }

  async function updateSubject(leadId, course_interest) {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, course_interest } : l))
    await supabase.from('leads').update({ course_interest: course_interest || null }).eq('id', leadId)
  }

  async function saveNote(leadId) {
    setSavingNote(true)
    await supabase.from('leads').update({ notes: noteText }).eq('id', leadId)
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, notes: noteText } : l))
    setSavingNote(false)
  }

  // Total and New cover every lead; Demo Scheduled and Enrolled count only
  // this salesperson's own leads.
  const total    = leads.length
  const newCount = leads.filter(l => l.status === 'New').length
  const mine     = leads.filter(l => l.assigned_to === profile.id)
  const demos    = mine.filter(l => l.status === 'Demo Scheduled').length
  const enrolled = mine.filter(l => l.status === 'Enrolled').length

  const byDate = sortBy === 'newest' || sortBy === 'oldest'
  const groups = []
  if (byDate) {
    for (const lead of sorted) {
      const k = dayKey(lead.created_at)
      const last = groups[groups.length - 1]
      if (last && last.key === k) last.leads.push(lead)
      else groups.push({ key: k, leads: [lead] })
    }
  } else {
    groups.push({ key: null, leads: sorted })
  }
  const isDayOpen = k => k === null || (openDays ? openDays.has(k) : k === groups[0]?.key) || !!q
  const toggleDay = k => setOpenDays(prev => {
    const next = new Set(prev || (groups[0] ? [groups[0].key] : []))
    next.has(k) ? next.delete(k) : next.add(k)
    return next
  })

  return (
    <div>
      {err && <div style={{ padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'13px', marginBottom:'10px' }}>{err}</div>}

      <Grid4>
        <MetricCard label="Total Leads"    value={total}    />
        <MetricCard label="New"            value={newCount} />
        <MetricCard label="My Demos Scheduled" value={demos}    />
        <MetricCard label="My Enrolled"        value={enrolled} />
      </Grid4>

      <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexDirection: isMobile ? 'column' : 'row' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={showContact ? '🔍  Search name, phone, email, source…' : '🔍  Search name, source, course…'}
          style={{ flex:1, padding:'10px 14px', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius:'10px', color:'var(--fg-solid)', fontSize:'13px', outline:'none' }}
        />
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          style={{ padding:'10px 14px', background:'rgba(var(--fg-rgb),0.07)', border:'0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius:'10px', color:'var(--fg-solid)', fontSize:'13px', cursor:'pointer', minWidth: isMobile ? '100%' : '180px' }}
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value} style={{ background:'var(--bg-input)' }}>{o.label}</option>
          ))}
        </select>
      </div>

      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'8px' }}>
        {q ? `${sorted.length} of ${total} leads` : `${total} leads`}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'40px', color:'rgba(var(--fg-rgb),var(--a30))' }}>Loading leads…</div>
      ) : sorted.length === 0 ? (
        <Empty>No leads found.</Empty>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {groups.map(g => (
            <React.Fragment key={g.key || 'all'}>
              {g.key && (
                <button type="button" onClick={() => toggleDay(g.key)} style={{ display:'flex', alignItems:'center', gap:'10px', width:'100%', padding:'10px 14px', marginTop:'6px', background:'rgba(30,144,255,0.06)', border:'0.5px solid rgba(30,144,255,0.18)', borderRadius:'12px', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                  <span style={{ fontSize:'13px', fontWeight:700, color:'var(--fg-solid)', flex:1 }}>📅 {g.key}</span>
                  <span style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a45))' }}>
                    {g.leads.length} lead{g.leads.length === 1 ? '' : 's'} · {g.leads.filter(l => l.status === 'New').length} new
                  </span>
                  <span style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))' }}>{isDayOpen(g.key) ? '▲' : '▼'}</span>
                </button>
              )}
          {isDayOpen(g.key) && g.leads.map(lead => {
            const idx    = sorted.indexOf(lead)
            const sc     = STATUS_META[lead.status] || STATUS_META['New']
            const isOpen = expandedId === lead.id
            const phone  = getPhone(lead)

            return (
              <div key={lead.id} style={{
                background: isOpen ? 'rgba(var(--fg-rgb),0.06)' : 'rgba(var(--fg-rgb),0.03)',
                border:`0.5px solid ${isOpen ? 'rgba(30,144,255,0.3)' : 'rgba(var(--fg-rgb),0.08)'}`,
                borderRadius:'14px', overflow:'hidden', transition:'all .2s',
              }}>
                <div
                  onClick={() => { setExpandedId(isOpen ? null : lead.id); setNoteText(lead.notes || '') }}
                  style={{ display:'flex', alignItems:'center', gap: isMobile ? '8px' : '12px', padding: isMobile ? '11px 12px' : '13px 16px', cursor:'pointer', flexWrap: isMobile ? 'wrap' : 'nowrap' }}
                >
                  <div style={{ minWidth:'28px', height:'28px', borderRadius:'8px', background:'rgba(30,144,255,0.12)', color:'#5aabff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:800, flexShrink:0 }}>
                    {idx + 1}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize: isMobile ? '14px' : '15px', color:'var(--fg-solid)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {lead.full_name || '—'}
                    </div>
                    <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'1px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {showContact ? (lead.email || '—') : (lead.course_interest || lead.source || '—')}
                    </div>
                  </div>
                  {showContact && <a href={phone ? `tel:${phone}` : undefined} onClick={e => e.stopPropagation()} style={{ display:'flex', alignItems:'center', gap:'5px', background:'rgba(16,185,129,0.12)', border:'0.5px solid rgba(16,185,129,0.3)', borderRadius:'8px', padding: isMobile ? '6px 10px' : '5px 12px', fontSize: isMobile ? '13px' : '12px', fontWeight:700, color: phone ? '#34d399' : 'rgba(var(--fg-rgb),var(--a20))', textDecoration:'none', flexShrink:0, whiteSpace:'nowrap' }}>
                    📞 {isMobile ? (phone ? 'Call' : '—') : (phone || 'No phone')}
                  </a>}
                  <span style={{ fontSize:'10px', fontWeight:700, padding: isMobile ? '4px 8px' : '3px 10px', borderRadius:'20px', background:sc.bg, color:sc.text, textTransform:'uppercase', letterSpacing:'0.4px', flexShrink:0, whiteSpace:'nowrap' }}>
                    {isMobile ? sc.icon : `${sc.icon} ${lead.status || 'New'}`}
                  </span>
                  <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a20))', flexShrink:0 }}>
                    {isOpen ? '▲' : '▼'}
                  </div>
                </div>

                {isOpen && (
                  <div style={{ borderTop:'0.5px solid rgba(var(--fg-rgb),0.07)', padding: isMobile ? '14px' : '16px 20px', background:'rgba(0,0,0,0.15)', display:'flex', flexDirection:'column', gap:'16px' }}>
                    <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3,1fr)', gap:'8px' }}>
                      {[
                        ...(showContact ? [
                          { label:'Phone',   value:phone,            href:`tel:${phone}` },
                          { label:'Email',   value:lead.email,       href:`mailto:${lead.email}` },
                        ] : []),
                        { label:'Source',  value:lead.source },
                        { label:'City',    value:lead.city },
                        { label:'Added',   value:`${fmtDate(lead.created_at)} ${fmtTime(lead.created_at)}` },
                      ].map(item => (
                        <div key={item.label} style={{ background:'rgba(var(--fg-rgb),0.04)', borderRadius:'10px', padding:'8px 10px' }}>
                          <div style={{ fontSize:'9px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'3px', textTransform:'uppercase', letterSpacing:'0.5px' }}>{item.label}</div>
                          {item.href && item.value
                            ? <a href={item.href} style={{ fontSize:'13px', color:'#60a5fa', fontWeight:600, textDecoration:'none' }}>{item.value}</a>
                            : <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a70))', fontWeight:500 }}>{item.value || '—'}</div>
                          }
                        </div>
                      ))}
                      <div style={{ background:'rgba(var(--fg-rgb),0.04)', borderRadius:'10px', padding:'8px 10px' }}>
                        <div style={{ fontSize:'9px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'3px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Subject</div>
                        <input
                          key={lead.id + ':' + (lead.course_interest||'')}
                          type="text"
                          defaultValue={lead.course_interest || ''}
                          placeholder="e.g. Guitar"
                          onBlur={e => { if (e.target.value !== (lead.course_interest||'')) updateSubject(lead.id, e.target.value.trim()) }}
                          style={{ width:'100%', boxSizing:'border-box', background:'transparent', border:'none', padding:0, fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a70))', fontWeight:500, outline:'none', fontFamily:'inherit' }}
                        />
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'8px' }}>Update Status</div>
                      <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                        {STATUSES.map(s => (
                          <button key={s} onClick={() => updateStatus(lead.id, s)} disabled={statusSaving === lead.id}
                            style={{ padding:'5px 12px', borderRadius:'8px', fontSize:'11px', fontWeight:700, cursor:'pointer', background: lead.status === s ? (STATUS_META[s]?.bg || 'rgba(var(--fg-rgb),0.1)') : 'rgba(var(--fg-rgb),0.06)', color: lead.status === s ? (STATUS_META[s]?.text || '#fff') : 'rgba(var(--fg-rgb),var(--a40))', border: lead.status === s ? `1px solid ${STATUS_META[s]?.text || 'transparent'}` : '1px solid transparent', opacity: statusSaving === lead.id ? 0.5 : 1 }}
                          >{s}</button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'6px' }}>Notes</div>
                      <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={3} placeholder="Add notes about this lead…"
                        style={{ width:'100%', padding:'10px', boxSizing:'border-box', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', color:'var(--fg-solid)', fontSize:'13px', resize:'vertical', outline:'none', fontFamily:'inherit' }}
                      />
                      <button onClick={() => saveNote(lead.id)} disabled={savingNote}
                        style={{ marginTop:'6px', padding:'7px 18px', borderRadius:'8px', border:'none', background:'#1e90ff', color:'#fff', fontSize:'12px', fontWeight:700, cursor:'pointer', opacity: savingNote ? 0.6 : 1 }}
                      >{savingNote ? 'Saving…' : 'Save Note'}</button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}
